import { isPrivilegedRole } from "@adpilot/shared";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ControlButton } from "@/components/patterns/control-button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { fetchWorkspaceProfile, updateWorkspaceName } from "@/lib/workspace-api";
import { useAuthStore } from "@/stores/auth";

export function WorkspaceProfilePanel(): React.ReactElement {
  const { t } = useTranslation("settings");
  const { t: tCommon } = useTranslation("common");
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const queryClient = useQueryClient();
  const activeWorkspace = user?.workspaces?.find((item) => item.id === user.activeWorkspaceId);
  const [name, setName] = useState(activeWorkspace?.name ?? "");
  const [copied, setCopied] = useState(false);
  const canManage = isPrivilegedRole(activeWorkspace?.role);
  const profileQuery = useQuery({
    queryKey: ["workspaces", activeWorkspace?.id, "profile"],
    queryFn: () => fetchWorkspaceProfile(activeWorkspace!.id),
    enabled: Boolean(activeWorkspace?.id),
  });

  useEffect(() => setName(activeWorkspace?.name ?? ""), [activeWorkspace?.name]);
  const mutation = useMutation({
    mutationFn: () => updateWorkspaceName(activeWorkspace!.id, name.trim()),
    onSuccess: (workspace) => {
      if (user) {
        setUser({
          ...user,
          workspaces: user.workspaces?.map((item) =>
            item.id === workspace.id ? { ...item, name: workspace.name } : item,
          ),
        });
      }
      queryClient.invalidateQueries({ queryKey: ["workspaces", activeWorkspace?.id] });
      toast.success(t("profile.nameUpdated"));
    },
    onError: () => toast.error(t("profile.nameUpdateError")),
  });

  if (!activeWorkspace) return <Skeleton className="h-[354px] rounded-[14px]" />;
  const profile = profileQuery.data;
  const created = profile
    ? new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric" }).format(
        new Date(profile.createdAt),
      )
    : "—";
  const ownerName =
    activeWorkspace.role === "owner"
      ? user?.name?.trim() || user?.email || t("profile.workspaceOwner")
      : t("profile.workspaceOwner");
  const ownerInitials = getInitials(ownerName);
  const unchanged = name.trim() === activeWorkspace.name;

  return (
    <div className="grid min-h-[354px] grid-cols-[minmax(0,1.99fr)_minmax(320px,1fr)] items-start gap-4">
      <section className="h-[354px] overflow-hidden rounded-[14px] border border-line bg-surface">
        <div className="flex h-[72px] flex-col justify-center gap-[3px] border-b border-line px-[18px]">
          <h2 className="text-[15px] font-semibold leading-5 text-ink">{t("profile.title")}</h2>
          <p className="text-[12px] leading-4 text-muted-foreground">{t("profile.subtitle")}</p>
        </div>

        <form
          className="flex h-[280px] flex-col gap-[18px] px-[18px] pb-[18px] pt-5"
          onSubmit={(event) => {
            event.preventDefault();
            mutation.mutate();
          }}
        >
          <Field className="h-[85px] gap-[7px]">
            <FieldLabel className="text-[12px] leading-4">{t("profile.nameLabel")}</FieldLabel>
            <Input
              type="text"
              size="lg"
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={!canManage}
              className="h-10 text-[13px]"
            />
            <FieldDescription className="text-[11px] leading-[15px]">
              {t("profile.nameDescription")}
            </FieldDescription>
          </Field>

          <Field className="h-[85px] gap-[7px]">
            <FieldLabel className="text-[12px] leading-4">{t("profile.idLabel")}</FieldLabel>
            <div className="flex h-10 w-full items-center justify-between rounded-[8px] border border-line bg-subtle pl-[11px] pr-2.5">
              <code className="min-w-0 truncate font-mono text-[12px] leading-4 text-muted-foreground">
                {activeWorkspace.id}
              </code>
              <ControlButton
                type="button"
                controlSize="compact"
                variant="outline"
                className="ml-3 shrink-0"
                onClick={() => {
                  void navigator.clipboard.writeText(activeWorkspace.id).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1600);
                  });
                }}
              >
                {copied ? (
                  <IconCheck data-icon="inline-start" />
                ) : (
                  <IconCopy data-icon="inline-start" />
                )}
                {copied ? t("profile.copied") : t("profile.copy")}
              </ControlButton>
            </div>
            <FieldDescription className="text-[11px] leading-[15px]">
              {t("profile.idDescription")}
            </FieldDescription>
          </Field>

          {canManage ? (
            <div className="flex h-10 items-center justify-end gap-2">
              <ControlButton
                type="button"
                controlSize="form"
                variant="outline"
                disabled={unchanged}
                onClick={() => setName(activeWorkspace.name)}
              >
                {tCommon("actions.cancel")}
              </ControlButton>
              <ControlButton
                type="submit"
                controlSize="form"
                loading={mutation.isPending}
                disabled={name.trim().length === 0 || unchanged}
              >
                {t("profile.saveChanges")}
              </ControlButton>
            </div>
          ) : (
            <p className="flex h-8 items-center text-[11px] text-muted-foreground">
              {t("profile.readOnly")}
            </p>
          )}
        </form>
      </section>

      <aside className="flex min-w-0 flex-col gap-3">
        <section className="overflow-hidden rounded-[14px] border border-line bg-surface">
          <div className="flex h-[61px] items-center gap-2.5 border-b border-line px-3.5">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-[9px] bg-ink text-[11px] font-semibold text-surface">
              {getInitials(activeWorkspace.name)}
            </span>
            <span className="flex min-w-0 flex-col gap-px">
              <span className="truncate text-[13px] font-semibold leading-[18px] text-ink">
                {activeWorkspace.name}
              </span>
              <span className="text-[11px] leading-[15px] text-muted-foreground">
                {t("profile.created", { date: created })}
              </span>
            </span>
          </div>
          <div className="flex flex-col px-3.5 py-1">
            <WorkspaceFact label={t("profile.workspaceOwner")}>
              <span className="flex items-center gap-[7px]">
                <span className="flex size-5 items-center justify-center rounded-full bg-subtle text-[8px] font-semibold text-ink">
                  {ownerInitials}
                </span>
                <span className="max-w-[160px] truncate">{ownerName}</span>
              </span>
            </WorkspaceFact>
            <WorkspaceFact label={t("profile.membersLabel")} divider>
              {profile ? t("profile.memberCount", { count: profile.memberCount }) : "—"}
            </WorkspaceFact>
            <WorkspaceFact label={t("profile.connectedShops")} divider={false}>
              {profile ? t("profile.shopCount", { count: profile.shopCount }) : "—"}
            </WorkspaceFact>
          </div>
        </section>
      </aside>
    </div>
  );
}

function WorkspaceFact({
  label,
  divider = true,
  children,
}: {
  label: string;
  divider?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div
      className={cn(
        "flex min-h-9 items-center justify-between",
        divider && "border-b border-subtle",
      )}
    >
      <span className="text-[11px] leading-[15px] text-muted-foreground">{label}</span>
      <span className="text-[11px] font-medium leading-[15px] text-ink">{children}</span>
    </div>
  );
}

function getInitials(value: string): string {
  return (
    value
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "WS"
  );
}
